import React, { useState, useEffect, useMemo } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Button,
  Paper,
  styled,
  Tabs,
  Tab,
  Divider,
  CircularProgress,
  Chip,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Alert,
  ButtonGroup,
  Tooltip,
  IconButton,
  TextField,
  Autocomplete,
  TableFooter
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useParams } from 'react-router-dom';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import InventoryIcon from '@mui/icons-material/Inventory';
import PersonIcon from '@mui/icons-material/Person';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import PaymentIcon from '@mui/icons-material/Payment';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DateRangeIcon from '@mui/icons-material/DateRange';
import EventNoteIcon from '@mui/icons-material/EventNote';
import DownloadIcon from '@mui/icons-material/Download';
import ownAuthSession from '../../../../services/auth/ownAuthSession';
import firebaseDataService from '../../../../services/FirebaseDataService';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker, DatePickerProps } from '@mui/x-date-pickers/DatePicker';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'; // ✅ YENİ: Saat seçimi için
import { tr } from 'date-fns/locale'; // Türkçe yerelleştirme için
import PosReportTab from './PosReportTab'; // Yeni import
import StockReportTab from './StockReportTab'; // Yeni Stok Raporu importu
import AccountReportTab from './AccountReportTab'; // Yeni Cari Raporu importu
import { utils, writeFile } from 'xlsx';
import { salesApi, accountingApi } from '../../../../config/apiClient';
import { useTranslation } from 'react-i18next';

// ScrollableContent bileşeni (QuickSalePage.tsx'ten alındı)
const ScrollableContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  overflowY: 'auto', 
  height: 'calc(100vh - 64px)', // Header yüksekliğini çıkarıyoruz
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#888',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: '#555',
  },
}));

const HeaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
}));

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`report-tabpanel-${index}`}
      aria-labelledby={`report-tab-${index}`}
      {...other}
      style={{ paddingTop: '16px' }}
    >
      {value === index && (
        <Box>
          {children}
        </Box>
      )}
    </div>
  );
}

// Ödeme seçeneği tiplerini tanımlama
type PaymentOptionType = 'cash' | 'pos' | 'openAccount' | 'voucher' | 'receipt' | 'bonus' | 'accrual';

// Ödeme seçeneği interface'i
interface PaymentOption {
  id: string;
  type: PaymentOptionType;
  label: string;
  enabled: boolean;
  cashboxId?: string | null;
  bankAccountId?: string | null;
  defaultAccountId?: string | null;
  timestamp?: number;
  subAccountId?: string | null;
  relatedAccount?: string;
}

// Kasa bilgisi interface'i
interface Cashbox {
  id: string;
  name: string;
}

// Kasa hareketi arayüzü
interface CashboxTransaction {
  id: string;
  cashboxId: string;
  cashboxName: string;
  type: 'INCOME' | 'EXPENSE'; // INCOME: Gelir, EXPENSE: Gider
  transactionType: string;
  amount: number;
  description: string;
  referenceId?: string;
  referenceType?: string;
  accountId?: string;
  accountName?: string;
  date: string; // Ana tarih (gün/ay/yıl)
  createdAt: string; // Tam zaman damgası (saat dahil)
  createdBy: string;
  currency: string;
  paymentMethod?: string;
  balanceBefore?: number; // İşlem öncesi kasa bakiyesi
  balanceAfter?: number; // İşlem sonrası kasa bakiyesi
  screenId?: string;
}

// Banka Hesabı ve Alt Hesap arayüzleri (PykScreenSettingsModal.tsx'den referans alınabilir)
interface SubAccountForModal {
  id: string;
  accountName: string;
  accountNumber: string;
  isPosAccount?: boolean;
}

interface BankAccount {
  id: string;
  bankName: string;
  accountNumber: string;
  isPosAccount?: boolean;
  subAccounts?: { [key: string]: SubAccountForModal };
}

// İşlem türleri
const TRANSACTION_TYPES = {
  CASH: 'cash',
  CREDIT_CARD: 'credit_card',
  DEBIT_CARD: 'DEBIT_CARD',
  BANK_TRANSFER: 'BANK_TRANSFER',
  COLLECTION: 'COLLECTION',
  PAYMENT: 'PAYMENT',
  DEPOSIT: 'DEPOSIT',
  WITHDRAWAL: 'WITHDRAWAL',
  EXPENSE: 'EXPENSE',
  INVOICE_PAYMENT: 'invoice_payment',
  QUICK_SALE: 'QUICK_SALE'
};

interface SalesScreenReportsPageProps {
  // İlerleyen aşamalarda gerekli olabilecek propslar buraya eklenebilir
}

const SalesScreenReportsPage: React.FC<SalesScreenReportsPageProps> = () => {
  const { t } = useTranslation('salesSystem');
  const navigate = useNavigate();
  const { screenId, screenType } = useParams<{ screenId: string, screenType: string }>();
  const [tabValue, setTabValue] = useState(0);
  const [screenName, setScreenName] = useState<string>("");
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [cashboxes, setCashboxes] = useState<Cashbox[]>([]);
  const [assignedCashboxes, setAssignedCashboxes] = useState<Cashbox[]>([]);
  const [allBankAccounts, setAllBankAccounts] = useState<BankAccount[]>([]);
  const [paymentOptionsFromScreen, setPaymentOptionsFromScreen] = useState<PaymentOption[]>([]);
  
  // Kasa hareketleri için state
  const [transactions, setTransactions] = useState<CashboxTransaction[]>([]);
  const [filteredTransactions, setFilteredTransactions] = useState<CashboxTransaction[]>([]);
  const [loadingTransactions, setLoadingTransactions] = useState<boolean>(false);
  const [transactionsError, setTransactionsError] = useState<string | null>(null);
  
  // Sayfalama için state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // Tarih filtresi için state
  const [dateFilter, setDateFilter] = useState<'today' | 'week' | 'month' | null>(null);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [selectedCashier, setSelectedCashier] = useState<string | null>(null);

  // Sekme değişikliğini handle et
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    
    // Eğer Kasa Raporu sekmesi seçilirse ve henüz kasa hareketleri yüklenmemişse
    if (newValue === 0 && transactions.length === 0 && assignedCashboxes.length > 0) {
      loadCashboxTransactions();
    }
  };

  // Kullanıcının şirket ID'sini al
  useEffect(() => {
    const fetchCompanyId = async () => {
      const uid = ownAuthSession.getCurrentUid();
      if (!uid) {
        setError(t('salesScreenReports.errors.userNotFound'));
        setLoading(false);
        return;
      }

      try {
        const userData = await firebaseDataService.getUserData(uid);
        
        if (userData) {
          setCompanyId(userData.companyId);
        } else {
          setError(t('salesScreenReports.errors.userInfoNotFound'));
          setLoading(false);
        }
      } catch (error) {
        console.error("Şirket ID alınırken hata:", error);
        setError(t('salesScreenReports.errors.companyInfoNotFound'));
        setLoading(false);
      }
    };

    fetchCompanyId();
  }, []);

  // Ekran bilgilerini ve ayarlarını çek - SQL'den
  useEffect(() => {
    const fetchScreenInfo = async () => {
      if (!screenId || !screenType || !companyId) {
        if (!screenId || !screenType) {
          setError(t('salesScreenReports.errors.screenInfoNotFound'));
        }
        return;
      }

      setError(null);
      setLoading(true);

      try {
        console.log("📋 Ekran bilgileri SQL'den yükleniyor...", { screenId, screenType, companyId });
        
        const screenResponse = await salesApi.get(`/api/QuickSaleScreen/${screenId}`);
        const screenResult = screenResponse.data;
        const screenData = screenResult.data || screenResult;
        
        // DTO'dan gelen property'ler büyük harfle başlıyor (C# convention)
        setScreenName(screenData.Name || t('salesScreenReports.common.unnamedScreen'));
        console.log("✅ Ekran bilgileri yüklendi:", screenData);
        
        // Ekran ayarlarını parse et (PaymentOptionsJson string olarak geliyor)
        let screenSettings: any = { paymentOptions: {} };
        if (screenData.PaymentOptionsJson) {
          try {
            screenSettings.paymentOptions = typeof screenData.PaymentOptionsJson === 'string' 
              ? JSON.parse(screenData.PaymentOptionsJson) 
              : screenData.PaymentOptionsJson;
            console.log("✅ Ödeme seçenekleri parse edildi:", screenSettings.paymentOptions);
          } catch (e) {
            console.warn("⚠️ Ödeme seçenekleri parse edilemedi:", e);
          }
        } else {
          // Ödeme seçenekleri artık SQL'de (PaymentOptionsJson). RTDB geçiş fallback'i kaldırıldı.
          console.warn("⚠️ PaymentOptionsJson bulunamadı; RTDB fallback kaldırıldı (geriye veri taşınmadı).");
        }
        
        const cashboxResponse = await accountingApi.get(`/api/Cashbox`);
        let cashboxesList: Cashbox[] = [];
        const cashboxResult = cashboxResponse.data;
          const sqlCashboxes = cashboxResult.data || cashboxResult;
          
          cashboxesList = sqlCashboxes.map((data: any) => ({
            id: data.id.toString(),
            name: data.name || t('salesScreenReports.common.unnamedCashbox')
          }));
          
          setCashboxes(cashboxesList);
          console.log("✅ Kasalar yüklendi:", cashboxesList.length, "adet");
        
        const bankResponse = await accountingApi.get(`/api/BankAccount`);
        let bankAccountsList: BankAccount[] = [];
        const bankResult = bankResponse.data;
          const sqlBankAccounts = bankResult.data || bankResult;
          
          bankAccountsList = sqlBankAccounts.map((data: any) => {
            const subAccounts: { [key: string]: SubAccountForModal } = {};
            
            // Alt hesapları işle
            if (data.subAccounts && Array.isArray(data.subAccounts)) {
              data.subAccounts.forEach((subData: any) => {
                subAccounts[subData.id.toString()] = {
                  id: subData.id.toString(),
                  accountName: subData.accountName || t('salesScreenReports.common.unnamedSubAccount'),
                  accountNumber: subData.accountNumber || '-',
                  isPosAccount: subData.isPosAccount || false,
                };
              });
            }
            
            return {
              id: data.id.toString(),
              bankName: data.bankName || t('salesScreenReports.common.unnamedBank'),
              accountNumber: data.accountNumber || '',
              isPosAccount: data.isPosAccount || false,
              subAccounts: Object.keys(subAccounts).length > 0 ? subAccounts : {}
            };
          });
          
          setAllBankAccounts(bankAccountsList);
          console.log("✅ Banka hesapları yüklendi:", bankAccountsList.length, "adet");
        
        // 4. Ekran ayarlarından ödeme seçeneklerini al ve atanmış kasaları bul
        if (screenSettings.paymentOptions) {
          const paymentOptionsData = screenSettings.paymentOptions;
          console.log("Ödeme seçenekleri (ham veri):", paymentOptionsData);
          
          // Ödeme seçeneklerini düzenli formata dönüştür
          const paymentOptions = Object.entries(paymentOptionsData).map(([id, option]: [string, any]) => ({
            id,
            ...option
          }));
          console.log("Ödeme seçenekleri (düzenlenmiş):", paymentOptions);
          setPaymentOptionsFromScreen(paymentOptions);
          
          // Cash tipindeki ve enabled olan ödeme seçeneklerini bul
          const cashPaymentOptions = paymentOptions.filter(option => 
            option.type === 'cash' && 
            option.enabled === true && 
            option.cashboxId
          );
          
          console.log("Nakit ödeme seçenekleri:", cashPaymentOptions);
          
          // Sadece kasa ID'lerini al
          const cashboxIds = cashPaymentOptions.map(option => option.cashboxId);
          console.log("Nakit ödeme seçenekleri için kasa ID'leri:", cashboxIds);
          
          // Ekrana atanmış kasaları bul
          if (cashboxesList.length > 0 && cashboxIds.length > 0) {
            const assignedCashboxesList = cashboxesList.filter(cashbox => 
              cashboxIds.includes(cashbox.id)
            );
            console.log("✅ Atanmış kasalar:", assignedCashboxesList);
            setAssignedCashboxes(assignedCashboxesList);
          } else {
            console.log("Atanmış kasa bulunamadı veya kasalar yüklenemedi");
            setAssignedCashboxes([]);
          }
        } else {
          console.log("Ekran ayarları veya ödeme seçenekleri bulunamadı");
          setAssignedCashboxes([]);
        }
        
      } catch (error) {
        console.error("❌ Ekran bilgisi alınırken hata oluştu:", error);
        setError(t('salesScreenReports.errors.screenInfoLoadFailed'));
        setScreenName(t('salesScreenReports.common.unnamedScreen'));
      } finally {
        setLoading(false);
      }
    };

    if (companyId) {
      fetchScreenInfo();
    }
  }, [screenId, screenType, companyId]);

  // Kasa hareketlerini yükle - useEffect hook'u (sadece bir kez çalışmalı)
  useEffect(() => {
    if (tabValue === 0 && !loadingTransactions && assignedCashboxes.length > 0 && transactions.length === 0) {
      console.log("Kasa hareketleri yükleniyor, atanmış kasalar:", assignedCashboxes);
      loadCashboxTransactions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue, assignedCashboxes.length]); // transactions.length ve loadingTransactions kaldırıldı (sonsuz döngü önlenir)
  
  // Kasa hareketlerini yükle - SQL'den
  const loadCashboxTransactions = async (cashboxList = assignedCashboxes) => {
    if (!companyId || cashboxList.length === 0) {
      console.log("Kasa hareketleri yüklenemedi: companyId yok veya atanmış kasa yok", { companyId, kasaSayisi: cashboxList.length });
      setTransactions([]);
      setFilteredTransactions([]);
      setSelectedCashier(null);
      setLoadingTransactions(false);
      return;
    }
    
    if (!screenId) {
      console.log("Kasa hareketleri yüklenemedi: screenId eksik.");
      setTransactions([]);
      setFilteredTransactions([]);
      setTransactionsError(t('salesScreenReports.errors.screenIdMissingForReport'));
      setLoadingTransactions(false);
      return;
    }
    
    setLoadingTransactions(true);
    setTransactionsError(null);
    
    try {
      console.log("📋 Kasa hareketleri SQL'den yükleniyor. Ekran ID:", screenId);
      
      const response = await accountingApi.get(`/api/Cashbox/transactions`);
      const result = response.data;
      const sqlTransactions = result.data || result;
      
      console.log(`✅ SQL'den ${sqlTransactions.length} kasa hareketi yüklendi`);
      
      if (!sqlTransactions || sqlTransactions.length === 0) {
        setTransactions([]);
        setFilteredTransactions([]);
        setLoadingTransactions(false);
        return;
      }
      
      // Atanmış kasa ID'lerini al
      const assignedCashboxIds = cashboxList.map(c => c.id);
      
      // SQL verilerini frontend formatına çevir ve filtrele
      const allTransactions: CashboxTransaction[] = sqlTransactions
        .filter((transaction: any) => {
          // Hem atanmış kasalardan biri olmalı hem de screenId eşleşmeli
          const cashboxMatch = assignedCashboxIds.includes(transaction.cashboxId?.toString());
          // ScreenId karşılaştırması: string '3' === string '3' veya string '3' === int 3
          const screenMatch = transaction.screenId?.toString() === screenId?.toString();
          
          console.log(`İşlem ${transaction.id}: cashboxId=${transaction.cashboxId}, screenId=${transaction.screenId}, cashboxMatch=${cashboxMatch}, screenMatch=${screenMatch}`);
          
          return cashboxMatch && screenMatch;
        })
        .map((transaction: any) => ({
          id: transaction.id.toString(),
          cashboxId: transaction.cashboxId?.toString() || '',
          cashboxName: transaction.cashbox?.name || t('salesScreenReports.common.unknownCashbox'),
          type: transaction.type || 'INCOME',
          transactionType: transaction.transactionType || '',
          amount: parseFloat(transaction.amount) || 0,
          description: transaction.description || '',
          referenceId: transaction.referenceId,
          referenceType: transaction.referenceType,
          accountId: transaction.accountId?.toString(),
          accountName: transaction.accountName,
          date: transaction.date || transaction.createdAt,
          createdAt: transaction.createdAt || '',
          createdBy: transaction.createdBy || '',
          currency: transaction.currency || 'TRY',
          paymentMethod: transaction.paymentMethod,
          balanceBefore: parseFloat(transaction.balanceBefore) || 0,
          balanceAfter: parseFloat(transaction.balanceAfter) || 0,
          screenId: transaction.screenId
        }));
      
      // Tarihe göre azalan sıralama (en yeni en üstte)
      allTransactions.sort((a, b) => {
        const dateA = a.createdAt || a.date;
        const dateB = b.createdAt || b.date;
        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;
        return new Date(dateB).getTime() - new Date(dateA).getTime();
      });
      
      console.log(`✅ ${allTransactions.length} kasa hareketi (ekran ID: ${screenId}) işlendi ve sıralandı`);
      
      setTransactions(allTransactions);
      setFilteredTransactions(allTransactions); 
    } catch (error) {
      console.error("❌ Kasa hareketleri alınırken hata:", error);
      setTransactionsError(t('salesScreenReports.errors.cashMovementsLoadFailed'));
      setTransactions([]);
      setFilteredTransactions([]);
    } finally {
      setLoadingTransactions(false);
    }
  };

  // Tarih ve Kasiyer filtresi uygulama fonksiyonu
  useEffect(() => {
    if (transactions.length === 0) {
      setFilteredTransactions([]);
      return;
    }
    
    let filtered = [...transactions];
    
    // Kasiyer Filtresi
    if (selectedCashier) {
      filtered = filtered.filter(transaction => transaction.createdBy === selectedCashier);
      console.log(`Kasiyer filtresi (${selectedCashier}) uygulandı. ${filtered.length} kayıt bulundu.`);
    }

    if (dateFilter) {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      let filterStartDate: Date;
      
      switch (dateFilter) {
        case 'today':
          filterStartDate = today;
          console.log(`Bugün filtresi uygulanıyor: ${today.toLocaleDateString()}`);
          break;
        case 'week':
          filterStartDate = new Date(today);
          filterStartDate.setDate(today.getDate() - 7);
          console.log(`Son hafta filtresi uygulanıyor: ${filterStartDate.toLocaleDateString()} - ${today.toLocaleDateString()}`);
          break;
        case 'month':
          filterStartDate = new Date(today);
          filterStartDate.setMonth(today.getMonth() - 1);
          console.log(`Son ay filtresi uygulanıyor: ${filterStartDate.toLocaleDateString()} - ${today.toLocaleDateString()}`);
          break;
        default:
          filterStartDate = new Date(0); 
          break;
      }
      
      filtered = filtered.filter(transaction => {
        if (!transaction.createdAt) return false;
        const transactionDate = new Date(transaction.createdAt);
        // 'today' filtresi için bitiş saati de önemli, bu yüzden gün sonuna kadar almalıyız
        if (dateFilter === 'today') {
          const endOfToday = new Date(today);
          endOfToday.setHours(23, 59, 59, 999);
          return transactionDate >= filterStartDate && transactionDate <= endOfToday;
        }
        return transactionDate >= filterStartDate;
      });
      console.log(`${dateFilter} filtresi uygulandı. ${filtered.length}/${transactions.length} kayıt gösteriliyor.`);

    } else if (startDate || endDate) {
      // ✅ DateTimePicker kullanıldığı için saat bilgisi korunuyor
      // Eğer sadece tarih seçildiyse (saat 00:00 ise), o günün başlangıcı/bitişi olarak ayarla
      const sDate = startDate ? new Date(startDate) : null;
      const eDate = endDate ? new Date(endDate) : null;

      filtered = filtered.filter(transaction => {
        if (!transaction.createdAt) return false;
        const transactionDate = new Date(transaction.createdAt);
        const afterStartDate = sDate ? transactionDate >= sDate : true;
        const beforeEndDate = eDate ? transactionDate <= eDate : true;
        return afterStartDate && beforeEndDate;
      });

      if (sDate && eDate) {
        console.log(`Tarih & Saat aralığı filtresi: ${sDate.toLocaleString('tr-TR')} - ${eDate.toLocaleString('tr-TR')}. ${filtered.length} kayıt bulundu.`);
      } else if (sDate) {
        console.log(`Başlangıç tarihi & saati filtresi: >= ${sDate.toLocaleString('tr-TR')}. ${filtered.length} kayıt bulundu.`);
      } else if (eDate) {
        console.log(`Bitiş tarihi & saati filtresi: <= ${eDate.toLocaleString('tr-TR')}. ${filtered.length} kayıt bulundu.`);
      }
    } else {
      console.log("Tarih filtresi yok, tüm kayıtlar gösteriliyor.");
    }
    
    setFilteredTransactions(filtered);
    setPage(0); 
  }, [transactions, dateFilter, startDate, endDate, selectedCashier]);

  // Sayfalandırılmış işlemler - transactions yerine filteredTransactions kullan
  const paginatedTransactions = useMemo(() => {
    return filteredTransactions.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredTransactions, page, rowsPerPage]);

  // Filtrelenmiş işlemlerin toplam tutarını hesapla
  const totalFilteredAmount = useMemo(() => {
    return filteredTransactions.reduce((sum, transaction) => {
      if (transaction.type === 'INCOME') {
        return sum + transaction.amount;
      } else if (transaction.type === 'EXPENSE') {
        return sum - transaction.amount;
      }
      return sum;
    }, 0);
  }, [filteredTransactions]);

  // Tablo için genel toplam tutar (sadece 'Tutar' sütunu için)
  const overallTotalCashAmount = useMemo(() => {
    return filteredTransactions.reduce((sum, transaction) => {
      // Bu, yukarıdaki totalFilteredAmount ile aynı mantıkta olmalı,
      // ancak net gelir/gideri yansıtmalı.
      // Eğer sadece pozitif ve negatif tutarların ayrı toplamları isteniyorsa mantık değişir.
      // Kullanıcı "rakam içeren sütunlardaki miktarları" dediği için,
      // ve "Tutar" sütunu zaten +/- gösterdiği için, buradaki toplam da net olmalı.
      if (transaction.type === 'INCOME') {
        return sum + transaction.amount;
      } else if (transaction.type === 'EXPENSE') {
        // Giderler zaten negatif olarak işlenmiyor, amount pozitif, type 'EXPENSE'
        // Bu yüzden totalFilteredAmount gibi net toplamı hesaplamak doğru
        return sum - transaction.amount;
      }
      return sum;
    }, 0);
  }, [filteredTransactions]);

  // Sayfa değişimini handle et
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Sayfa başına satır sayısı değişimini handle et
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Tarih formatla
  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('tr-TR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    } catch (error) {
      return dateString;
    }
  };

  // Para birimi formatla
  const formatCurrency = (amount: number, currencyCode: string = 'TRY') => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2
    }).format(amount);
  };

  // İşlem tipi için ikon döndüren fonksiyon
  const getTransactionIcon = (transaction: CashboxTransaction) => {
    const transType = transaction.transactionType?.toUpperCase() || '';
    const mainType = transaction.type || 'INCOME';

    // Önce doğrudan işlem türü eşleşmesi kontrolü
    if (transType in TRANSACTION_TYPES) {
      const type = TRANSACTION_TYPES[transType as keyof typeof TRANSACTION_TYPES];
      
      switch (type) {
        case TRANSACTION_TYPES.CASH:
        case TRANSACTION_TYPES.DEPOSIT:
        case TRANSACTION_TYPES.COLLECTION:
          return mainType === 'INCOME' ? <TrendingUpIcon fontSize="small" color="success" /> : <TrendingDownIcon fontSize="small" color="error" />;
        case TRANSACTION_TYPES.CREDIT_CARD:
          return <CreditCardIcon fontSize="small" color="primary" />;
        case TRANSACTION_TYPES.DEBIT_CARD:
          return <PaymentIcon fontSize="small" color="secondary" />;
        case TRANSACTION_TYPES.BANK_TRANSFER:
          return mainType === 'INCOME' ? <TrendingUpIcon fontSize="small" color="success" /> : <TrendingDownIcon fontSize="small" color="error" />;
        case TRANSACTION_TYPES.QUICK_SALE:
          return <PointOfSaleIcon fontSize="small" color="info" />;
        case TRANSACTION_TYPES.PAYMENT:
        case TRANSACTION_TYPES.WITHDRAWAL:
        case TRANSACTION_TYPES.EXPENSE:
          return <TrendingDownIcon fontSize="small" color="error" />;
      }
    }
    
    // Varsayılan ikon
    return mainType === 'INCOME' ? <TrendingUpIcon fontSize="small" color="success" /> : <TrendingDownIcon fontSize="small" color="error" />;
  };
  
  // İşlem adını döndüren fonksiyon
  const getTransactionTypeName = (transaction: CashboxTransaction): string => {
    const transType = transaction.transactionType?.toUpperCase() || '';

    const typeMap: Record<string, string> = {
      CASH: t('salesScreenReports.transactionTypes.cash'),
      CREDIT_CARD: t('salesScreenReports.transactionTypes.creditCard'),
      DEBIT_CARD: t('salesScreenReports.transactionTypes.debitCard'),
      BANK_TRANSFER: t('salesScreenReports.transactionTypes.bankTransfer'),
      COLLECTION: t('salesScreenReports.transactionTypes.collection'),
      PAYMENT: t('salesScreenReports.transactionTypes.payment'),
      DEPOSIT: t('salesScreenReports.transactionTypes.deposit'),
      WITHDRAWAL: t('salesScreenReports.transactionTypes.withdrawal'),
      EXPENSE: t('salesScreenReports.transactionTypes.expense'),
      INVOICE_PAYMENT: t('salesScreenReports.transactionTypes.invoicePayment'),
      QUICK_SALE: t('salesScreenReports.transactionTypes.quickSale'),
    };

    if (typeMap[transType]) return typeMap[transType];
    if (typeMap[(transaction.transactionType || '').toUpperCase()]) return typeMap[(transaction.transactionType || '').toUpperCase()];
    if (typeMap[(transaction.type || '').toUpperCase()]) return typeMap[(transaction.type || '').toUpperCase()];

    return transaction.transactionType || transaction.type || t('salesScreenReports.common.other');
  };

  const handleGoBack = () => {
    navigate('/hizli-satis');
  };

  // Aktif tarih filtresini göster
  const getDateFilterLabel = (): string => {
    if (dateFilter) {
    switch (dateFilter) {
      case 'today': return t('salesScreenReports.dateFilters.today');
      case 'week': return t('salesScreenReports.dateFilters.lastWeek');
      case 'month': return t('salesScreenReports.dateFilters.lastMonth');
      default: return '';
    }
    } else if (startDate && endDate) {
      return t('salesScreenReports.dateFilters.range', {
        start: startDate.toLocaleDateString(tr.code),
        end: endDate.toLocaleDateString(tr.code),
      });
    } else if (startDate) {
      return t('salesScreenReports.dateFilters.startOnly', { start: startDate.toLocaleDateString(tr.code) });
    } else if (endDate) {
      return t('salesScreenReports.dateFilters.endOnly', { end: endDate.toLocaleDateString(tr.code) });
    }
    return '';
  };

  // Benzersiz kasiyer isimlerini al
  const uniqueCashiers = useMemo(() => {
    if (transactions.length === 0) return [];
    const cashierSet = new Set(transactions.map((transaction) => transaction.createdBy || t('salesScreenReports.common.unknown')));
    return Array.from(cashierSet).sort();
  }, [transactions, t]);

  // Excel indirme fonksiyonu
  const exportCashboxTransactionsToExcel = () => {
    if (filteredTransactions.length === 0) return;
    
    // Excel verilerini hazırla
    const excelData = filteredTransactions.map(transaction => {
      return {
        [t('salesScreenReports.table.date')]: formatDate(transaction.createdAt),
        [t('salesScreenReports.table.cashbox')]: transaction.cashboxName || '',
        [t('salesScreenReports.table.transactionType')]: getTransactionTypeName(transaction),
        [t('salesScreenReports.table.description')]: transaction.description || '',
        [t('salesScreenReports.table.amount')]: `${transaction.type === 'INCOME' ? '+' : '-'} ${formatCurrency(transaction.amount, transaction.currency)}`,
        [t('salesScreenReports.table.balance')]: formatCurrency(transaction.balanceAfter || 0, transaction.currency),
        [t('salesScreenReports.table.cashier')]: transaction.createdBy || ''
      };
    });

    // Excel çalışma kitabı oluştur
    const wb = utils.book_new();
    const ws = utils.json_to_sheet(excelData);

    // Sütun genişliklerini ayarla
    const colWidths = [
      { wch: 20 }, // Tarih
      { wch: 15 }, // Kasa
      { wch: 15 }, // İşlem Türü
      { wch: 40 }, // Açıklama
      { wch: 15 }, // Tutar
      { wch: 15 }, // Bakiye
      { wch: 15 }  // Kasiyer
    ];
    ws['!cols'] = colWidths;

    // Çalışma sayfasını kitaba ekle
    utils.book_append_sheet(wb, ws, t('salesScreenReports.tabs.cashReport'));

    // Dosya adını hazırla
    const fileName = `Cash_Report_${new Date().toLocaleDateString('tr-TR').replace(/\./g, '-')}.xlsx`;

    // Excel dosyasını indir
    writeFile(wb, fileName);
  };

  return (
    <ScrollableContent>
      <HeaderContainer>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button 
            onClick={handleGoBack}
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            sx={{ mr: 2 }}
          >
            {t('salesScreenReports.actions.back')}
          </Button>
          <AssessmentIcon sx={{ fontSize: 32, mr: 1.5, color: 'primary.main' }} />
          <Typography variant="h5" component="h1" fontWeight="bold">
            {loading ? t('salesScreenReports.common.loading') : `${screenName} - ${t('salesScreenReports.titleSuffix')}`}
          </Typography>
        </Box>
      </HeaderContainer>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Paper elevation={3} sx={{ p: 3, mt: 2, borderRadius: 2 }}>
          <Typography variant="body1" color="error" align="center">
            {error}
          </Typography>
        </Paper>
      ) : (
        <>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              aria-label="sales reports tabs"
            >
              <Tab 
                icon={<AccountBalanceWalletIcon />} 
                iconPosition="start" 
                label={t('salesScreenReports.tabs.cashReport')}
                sx={{ textTransform: 'none', fontSize: '0.875rem' }} 
              />
              <Tab 
                icon={<CreditCardIcon />} 
                iconPosition="start" 
                label={t('salesScreenReports.tabs.posReport')}
                sx={{ textTransform: 'none', fontSize: '0.875rem' }} 
              />
              <Tab 
                icon={<InventoryIcon />} 
                iconPosition="start" 
                label={t('salesScreenReports.tabs.stockReport')}
                sx={{ textTransform: 'none', fontSize: '0.875rem' }} 
              />
              <Tab 
                icon={<PersonIcon />} 
                iconPosition="start" 
                label={t('salesScreenReports.tabs.accountReport')}
                sx={{ textTransform: 'none', fontSize: '0.875rem' }} 
              />
            </Tabs>
          </Box>

          <TabPanel value={tabValue} index={0}>
            <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                  {/* Sol Taraf: Başlık, Kasa Çipleri, Toplam Tutar */}
                  <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                    <Typography variant="h6" fontWeight="bold" sx={{ mr: 1 }}>
                      {t('salesScreenReports.tabs.cashReport')}
                    </Typography>
                    {assignedCashboxes.length > 0 ? (
                      assignedCashboxes.map((cashbox) => (
                        <Chip
                          key={cashbox.id}
                          label={cashbox.name}
                          color="primary"
                          variant="outlined"
                          size="small"
                          sx={{ mr:0.5, mb: 0.5 }}
                        />
                      ))
                    ) : (
                      <Chip
                        label={t('salesScreenReports.common.cashboxNotAssigned')}
                        variant="outlined"
                        size="small"
                        sx={{ color: 'text.secondary', mr: 1, mb: 0.5 }}
                      />
                    )}
                    {filteredTransactions.length > 0 && (
                      <Chip
                        label={`${t('salesScreenReports.summary.total')}: ${formatCurrency(totalFilteredAmount, 'TRY')}`}
                        color={totalFilteredAmount >= 0 ? "success" : "error"}
                        variant="filled"
                        size="small"
                        sx={{ fontWeight: 'bold', fontSize: '0.9rem', padding: '4px 8px', mr: 1, mb: 0.5 }}
                      />
                    )}
                  </Box>

                  {/* Sağ Taraf: Filtreler */}
                  <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, justifyContent: { xs: 'flex-start', md: 'flex-end' }, flexGrow: 1 }}>
                    {/* Kasiyer Filtresi */}
                    {uniqueCashiers.length > 0 && (
                      <Autocomplete
                        options={uniqueCashiers}
                        value={selectedCashier}
                        onChange={(event, newValue) => {
                          setSelectedCashier(newValue);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={t('salesScreenReports.filters.selectCashier')}
                            size="small"
                            variant="outlined"
                            sx={{ width: { xs: '100%', sm: 180 }, mr:1 }}
                          />
                        )}
                        sx={{ mb: { xs: 1, sm: 0 } }}
                      />
                    )}
                    {/* DateTimePicker Filtreleri - ✅ Saat ile */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1}}>
                      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={tr}>
                        <DateTimePicker
                          label={t('salesScreenReports.filters.startDateTime')}
                          value={startDate}
                          onChange={(newValue) => { setStartDate(newValue); setDateFilter(null); }}
                          maxDateTime={endDate || undefined}
                          ampm={false} // 24 saat formatı
                          format="dd.MM.yyyy HH:mm" // Türkiye formatı
                          slotProps={{
                            textField: {
                              size: 'small',
                              sx: { width: { xs: '100%', sm: 200 } }, // Saat için daha geniş
                              variant: 'outlined'
                            },
                          }}
                        />
                        <DateTimePicker
                          label={t('salesScreenReports.filters.endDateTime')}
                          value={endDate}
                          onChange={(newValue) => { setEndDate(newValue); setDateFilter(null); }}
                          minDateTime={startDate || undefined}
                          ampm={false} // 24 saat formatı
                          format="dd.MM.yyyy HH:mm" // Türkiye formatı
                          slotProps={{
                            textField: {
                              size: 'small',
                              sx: { width: { xs: '100%', sm: 200 } }, // Saat için daha geniş
                              variant: 'outlined'
                            },
                          }}
                        />
                      </LocalizationProvider>
                      {(startDate || endDate || selectedCashier) && (
                        <Tooltip title={t('salesScreenReports.actions.clearAllFilters')}>
                          <IconButton onClick={() => { setStartDate(null); setEndDate(null); setDateFilter(null); setSelectedCashier(null); }} size="small">
                            <FilterAltOffIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>

                    {/* Hazır Tarih Filtre Butonları */}
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {dateFilter && (
                        <Chip
                          icon={<FilterAltIcon />}
                          label={getDateFilterLabel()}
                          variant="filled"
                          color="primary"
                          size="small"
                          onDelete={() => setDateFilter(null)}
                          deleteIcon={<FilterAltOffIcon />}
                          sx={{ mr: 1.5 }} // PosReportTab'dan alındı
                        />
                      )}
                      <ButtonGroup size="small" variant="outlined">
                        <Tooltip title={t('salesScreenReports.tooltips.todayRecords')}>
                          <Button
                            onClick={() => { setDateFilter('today'); setStartDate(null); setEndDate(null); }}
                            color={dateFilter === 'today' ? 'primary' : 'inherit'}
                            variant={dateFilter === 'today' ? 'contained' : 'outlined'}
                            startIcon={<CalendarTodayIcon />}
                          >
                            {t('salesScreenReports.dateFilters.today')}
                          </Button>
                        </Tooltip>
                        <Tooltip title={t('salesScreenReports.tooltips.last7DaysRecords')}>
                          <Button
                            onClick={() => { setDateFilter('week'); setStartDate(null); setEndDate(null); }}
                            color={dateFilter === 'week' ? 'primary' : 'inherit'}
                            variant={dateFilter === 'week' ? 'contained' : 'outlined'}
                            startIcon={<DateRangeIcon />}
                          >
                            {t('salesScreenReports.dateFilters.lastWeek')}
                          </Button>
                        </Tooltip>
                        <Tooltip title={t('salesScreenReports.tooltips.last30DaysRecords')}>
                          <Button
                            onClick={() => { setDateFilter('month'); setStartDate(null); setEndDate(null); }}
                            color={dateFilter === 'month' ? 'primary' : 'inherit'}
                            variant={dateFilter === 'month' ? 'contained' : 'outlined'}
                            startIcon={<EventNoteIcon />}
                          >
                            {t('salesScreenReports.dateFilters.lastMonth')}
                          </Button>
                        </Tooltip>
                      </ButtonGroup>
                      
                      {/* Excel İndirme Butonu */}
                      <Button
                        variant="contained"
                        color="info"
                        startIcon={<DownloadIcon />}
                        onClick={exportCashboxTransactionsToExcel}
                        disabled={filteredTransactions.length === 0}
                        sx={{ ml: 2 }}
                      >
                        {t('salesScreenReports.actions.downloadExcel')}
                      </Button>
                    </Box>
                  </Box>
                </Box>
                <Divider sx={{ mb: 2 }} />
              </Box>
              
              {assignedCashboxes.length === 0 ? (
                <Typography variant="body1" align="center" sx={{ py: 4 }}>
                  {t('salesScreenReports.emptyState.noAssignedCashbox')}
                </Typography>
              ) : loadingTransactions ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : transactionsError ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {transactionsError}
                </Alert>
              ) : filteredTransactions.length === 0 ? (
                <Typography variant="body1" align="center" sx={{ py: 4 }}>
                  {dateFilter || startDate || endDate || selectedCashier
                    ? t('salesScreenReports.emptyState.noRecordsForCriteria')
                    : t('salesScreenReports.emptyState.noCashboxRecords')}
                </Typography>
              ) : (
                <>
                  <TableContainer>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', bgcolor: 'secondary.main', color: 'white', width: '15%' }}>{t('salesScreenReports.table.date')}</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', bgcolor: 'secondary.main', color: 'white', width: '15%' }}>{t('salesScreenReports.table.cashbox')}</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', bgcolor: 'secondary.main', color: 'white', width: '15%' }}>{t('salesScreenReports.table.transactionType')}</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', bgcolor: 'secondary.main', color: 'white', width: '25%' }}>{t('salesScreenReports.table.description')}</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', bgcolor: 'secondary.main', color: 'white', width: '15%', textAlign: 'right' }}>{t('salesScreenReports.table.amount')}</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', bgcolor: 'secondary.main', color: 'white', width: '10%', textAlign: 'right' }}>{t('salesScreenReports.table.balance')}</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', bgcolor: 'secondary.main', color: 'white', width: '15%' }}>{t('salesScreenReports.table.cashier')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedTransactions.map((transaction) => (
                          <TableRow 
                            key={transaction.id}
                            hover
                            sx={{ 
                              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                              backgroundColor: transaction.type === 'INCOME' ? 'rgba(237, 247, 237, 0.4)' : 
                                               (transaction.type === 'EXPENSE' ? 'rgba(253, 237, 237, 0.4)' : undefined)
                            }}
                          >
                            <TableCell sx={{ fontSize: '0.875rem' }}>{formatDate(transaction.createdAt)}</TableCell>
                            <TableCell sx={{ fontSize: '0.875rem' }}>{transaction.cashboxName}</TableCell>
                            <TableCell sx={{ fontSize: '0.875rem' }}>
                              <Chip
                                icon={getTransactionIcon(transaction)}
                                label={getTransactionTypeName(transaction)}
                                size="small"
                                color={transaction.type === 'INCOME' ? 'success' : 'error'}
                                sx={{ fontSize: '0.75rem' }}
                              />
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.875rem' }}>{transaction.description}</TableCell>
                            <TableCell sx={{ 
                              fontSize: '0.875rem', 
                              fontWeight: 'medium',
                              color: transaction.type === 'INCOME' ? 'success.main' : 'error.main',
                              textAlign: 'right' // Sağa yaslama eklendi
                            }}>
                              {transaction.type === 'INCOME' ? '+' : '-'} 
                              {formatCurrency(transaction.amount, transaction.currency)}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.875rem', textAlign: 'right' }}> {/* Sağa yaslama eklendi */}
                              {formatCurrency(transaction.balanceAfter || 0, transaction.currency)}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.875rem' }}>{transaction.createdBy}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                      <TableFooter>
                        <TableRow sx={{ '& td': { fontWeight: 'bold', fontSize: '0.9rem' } }}>
                          <TableCell colSpan={4} sx={{ textAlign: 'right' }}>{t('salesScreenReports.summary.grandTotal')}</TableCell>
                          <TableCell sx={{ textAlign: 'right', color: overallTotalCashAmount >= 0 ? 'success.main' : 'error.main' }}>
                            {formatCurrency(overallTotalCashAmount, 'TRY')}
                          </TableCell>
                          <TableCell></TableCell> {/* Bakiye sütunu için boş */ }
                          <TableCell></TableCell> {/* Kasiyer sütunu için boş */ }
                        </TableRow>
                      </TableFooter>
                    </Table>
                  </TableContainer>
                  
                  <TablePagination
                    component="div"
                    count={filteredTransactions.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage={t('salesScreenReports.pagination.rowsPerPage')}
                    labelDisplayedRows={({ from, to, count }) => t('salesScreenReports.pagination.displayedRows', { count, from, to })}
                    rowsPerPageOptions={[5, 10, 25, 50, 100]}
                  />
                </>
              )}
            </Paper>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <PosReportTab
              paymentOptions={paymentOptionsFromScreen}
              allBankAccounts={allBankAccounts}
              companyId={companyId}
              screenId={screenId || null}
            />
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <StockReportTab companyId={companyId} screenId={screenId || null} />
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <AccountReportTab
              companyId={companyId}
              screenId={screenId || null}
            />
          </TabPanel>

          <Box sx={{ height: '400px' }} /> {/* Sayfa sonu boşluk eklendi */}
        </>
      )}
    </ScrollableContent>
  );
};

export default SalesScreenReportsPage; 